import React, { useState, useRef, useCallback, useImperativeHandle, forwardRef, useEffect } from 'react';
import { Send, Square, Paperclip, X, FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SimpleTooltip } from '@/components/overlays/simple-tooltip';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/core/utils';
import { parseFile, type ParsedFileData } from '@/lib/ai-chat/file-parser';
import { toast } from 'sonner';
import type { AIMessageInputProps } from './types';

export interface AIMessageInputHandle {
  focus: () => void;
}

const ACCEPTED_FILE_TYPES = '.csv,.xlsx,.xls,.json';

export const AIMessageInput = forwardRef<AIMessageInputHandle, AIMessageInputProps>(({
  onSend,
  onStop,
  disabled = false,
  isLoading = false,
  placeholder = 'Ask, search or make anything...',
  autoFocus = false,
  allowFileUpload = false,
}, ref) => {
  const [input, setInput] = useState('');
  const [attachedFile, setAttachedFile] = useState<ParsedFileData | null>(null);
  const [isParsingFile, setIsParsingFile] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    focus: () => {
      textareaRef.current?.focus();
    },
  }));

  // Auto-focus when autoFocus prop changes
  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      const timer = setTimeout(() => {
        textareaRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [autoFocus]);

  const handleFileSelect = useCallback(async (file: File) => {
    setIsParsingFile(true);
    try {
      const parsed = await parseFile(file);
      setAttachedFile(parsed);
    } catch (err) {
      toast.error('Failed to parse file', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setIsParsingFile(false);
    }
  }, []);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
    // Reset so the same file can be selected again
    e.target.value = '';
  }, [handleFileSelect]);

  const handleSubmit = useCallback(() => {
    const trimmedInput = input.trim();
    if ((!trimmedInput && !attachedFile) || disabled) return;

    const messageContent = trimmedInput || (attachedFile ? `Uploaded file: ${attachedFile.fileName}` : '');
    onSend(messageContent, attachedFile || undefined);
    setInput('');
    setAttachedFile(null);

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [input, disabled, onSend, attachedFile]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  };

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (!allowFileUpload) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, [allowFileUpload]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (!allowFileUpload) return;
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, [allowFileUpload, handleFileSelect]);

  return (
    <div
      className="p-3"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* File attachment badge */}
      {attachedFile && (
        <div className="flex items-center gap-2 mb-2 px-2">
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-primary/10 text-primary text-xs font-medium">
            <FileSpreadsheet className="h-3.5 w-3.5" />
            <span>{attachedFile.fileName}</span>
            <span className="text-muted-foreground">({attachedFile.rowCount} rows)</span>
            <Button
              onClick={() => setAttachedFile(null)}
              className="ml-1 hover:text-destructive transition-colors"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}

      <div className={cn(
        'flex gap-2 items-end rounded-2xl',
        'bg-muted/50 border border-border/50',
        'p-2',
        isDragOver && 'border-primary/50 bg-primary/5',
      )}>
        {/* File upload button */}
        {allowFileUpload && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_FILE_TYPES}
              onChange={handleFileInputChange}
              className="hidden"
            />
            <SimpleTooltip content="Attach file (CSV, XLSX, JSON)">
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled || isParsingFile}
                className="h-9 w-9 shrink-0 rounded-xl text-muted-foreground hover:text-foreground"
              >
                <Paperclip className={cn('h-4 w-4', isParsingFile && 'animate-pulse')} />
                <span className="sr-only">Attach file</span>
              </Button>
            </SimpleTooltip>
          </>
        )}

        <Textarea
          ref={textareaRef}
          value={input}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            'min-h-[36px] max-h-[120px] resize-none flex-1',
            'text-base md:text-sm py-2 px-3',
            'bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
          rows={1}
        />
        {isLoading && onStop ? (
          <SimpleTooltip content="Stop generation">
            <Button
              size="icon"
              onClick={onStop}
              variant="destructive"
              className="h-9 w-9 shrink-0 rounded-xl"
            >
              <Square className="h-4 w-4" />
              <span className="sr-only">Stop generation</span>
            </Button>
          </SimpleTooltip>
        ) : (
          <Button
            size="icon"
            onClick={handleSubmit}
            disabled={disabled || (!input.trim() && !attachedFile)}
            className="h-9 w-9 shrink-0 rounded-xl"
          >
            <Send className="h-4 w-4" />
            <span className="sr-only">Send message</span>
          </Button>
        )}
      </div>
    </div>
  );
});

AIMessageInput.displayName = 'AIMessageInput';

export default AIMessageInput;
